import { api, Product, responseErrorCheck, TmrBaseResource, TmrProduct } from 'stylewhere/api'
import { config } from 'stylewhere/config'
import { AppStore } from 'stylewhere/shared'
import Images from './Images'
import { PrivateImageCache, privateImageCache } from './PrivateImageCache'

export class Products extends TmrBaseResource {
  static readonly endpoint = 'api/v1/products' //tracking

  static searchCodes(codes: string[]) {
    return this.search<TmrProduct>({ equalCodes: codes })
  }

  static async getProductsByCodes(upcs: string[]): Promise<Product[]> {
    return api
      .post<Product>(`${this.endpoint}/advancedSearch`, {
        codes: upcs,
        limit: 50000
      })
      .then(responseErrorCheck)
  }

  private static readonly getImageIdFromAttributes = (attributes: any): string => {
    if (attributes?.pictureId?.value) return attributes.pictureId.value
    if (attributes?.sketchId?.value) return attributes.sketchId.value
    if (attributes?.imageId?.value) return attributes.imageId.value
    return undefined
  }

  private static readonly getImageIdFromLegacyFields = (record: any): string => {
    if (record._ImageId) return record._ImageId
    if (record._SkatchId) return record._SkatchId
    return undefined
  }

  private static readonly getImageIdFromCode = (record: any): string => {
    if (record.code) return `IMG-${record.code}`
    if (record.productCode) return `IMG-${record.productCode}`
    return undefined
  }

  static readonly getImageID = (record: any) => {
    if (!record) return ''

    let imageId = this.getImageIdFromAttributes(record.attributes) || this.getImageIdFromLegacyFields(record)

    if (!imageId) {
      imageId = this.getImageIdFromCode(record)
    }

    return imageId
  }

  static readonly isProductImage = (image: string): boolean => {
    return image.includes('http') && image.includes('product-images')
  }

  static readonly parseImageUrl = (publicUrl: string, size: string): string => {
    try {
      if (!publicUrl.includes('{')) {
        // Pulisce la stringa rimuovendo tutto quello che viene dopo " - "
        const cleanedUrl = publicUrl.includes(' - ') ? publicUrl.substring(0, publicUrl.indexOf(' - ')) : publicUrl
        return cleanedUrl
      }

      const lastBraceIndex = publicUrl.lastIndexOf('}')
      const cleanedUrl = lastBraceIndex !== -1 ? publicUrl.substring(0, lastBraceIndex + 1) : publicUrl

      const decode = JSON.parse(cleanedUrl)
      if (decode?.imageUrls?.[size || 'large']) {
        return decode.imageUrls[size || 'large']
      }
    } catch (_e) {
      console.error(_e)
    }
    return ''
  }

  static readonly extractImageIdFromImage = (image: string): string => {
    if (image.includes('http')) {
      const match = image.match(/([a-f0-9-]{36})(?:\.\w+)?$/i)
      if (match) {
        return match[1]
      }
    }
    return image
  }

  private static readonly buildPrivateImageUrl = (imageId: string, type: string): string => {
    const baseURL = config.endpoint
    return `${baseURL}${Images.endpoint}?imageId=${encodeURIComponent(decodeURIComponent(imageId))}&size=${type}`
  }

  private static readonly fetchPrivateImageRaw = async (imageId: string, type: string): Promise<string> => {
    if (!AppStore.authToken) return ''
    const url = Products.buildPrivateImageUrl(imageId, type)
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${AppStore.authToken}` }
      })
      if (!response.ok) return ''
      const blob = await response.blob()
      return URL.createObjectURL(blob)
    } catch {
      return ''
    }
  }

  /**
   * Recupera l'object URL dell'immagine privata usando una cache LRU in memoria,
   * così da evitare di riscaricare la stessa immagine (e ripetere la chiamata al
   * servizio esterno del cliente) ad ogni render.
   *
   * ATTENZIONE: l'object URL restituito è di proprietà della cache. Il chiamante
   * NON deve invocare URL.revokeObjectURL su di esso: la revoca avviene solo in
   * fase di eviction LRU.
   */
  static readonly fetchPrivateImage = async (imageId: string, type: string): Promise<string> => {
    if (!AppStore.authToken) return ''
    const key = PrivateImageCache.buildKey(imageId, type)
    return privateImageCache.resolve(key, () => Products.fetchPrivateImageRaw(imageId, type))
  }

  static readonly getPublicImageUrl = (product: any, type: string): string => {
    let publicUrl = ''
    if (product.attributes?.imageId?.value) {
      publicUrl = product.attributes.imageId.value
    } else if (product._ImageId) {
      publicUrl = product._ImageId.value || product._ImageId
    }

    if (!publicUrl) return ''

    try {
      const decode = JSON.parse(publicUrl)
      return decode?.imageUrls?.[type] || ''
    } catch (e) {
      console.error(e)
      return ''
    }
  }
}
